import React, { useState } from "react";
import {
  Alert,
  Button,
  CircularProgress,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { AutoAwesomeRounded, SaveRounded } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import useSnackbar from "../hooks/useSnackbar";
import { PageIntro, PageShell, SurfaceCard } from "../ui/AppPrimitives";

export const AddDate = ({ addDate }) => {
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();

  const [type, setType] = useState("single");
  const [title, setTitle] = useState("");
  const [date, setDate] = useState("");
  const [category, setCategory] = useState("Personal");
  const [notes, setNotes] = useState("");
  const [showError, setShowError] = useState("");
  const [time, setTime] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();

    if (!title.trim() || !date) {
      setShowError("Event title and date are required.");
      return;
    }

    setSaving(true);
    const dateTime = time ? `${date}T${time}` : `${date}T00:00`;
    const selectedDate = new Date(dateTime);
    const today = new Date();
    selectedDate.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);

    try {
      await addDate({
        title: title.trim(),
        dateTime,
        category,
        notes: notes.trim(),
        type,
        recurrence: type === "recurring" ? "yearly" : null,
      });

      showSnackbar("Date added successfully!", "success");

      if (type === "recurring") {
        navigate("/celebrations");
      } else if (selectedDate < today) {
        navigate("/completed");
      } else {
        navigate("/");
      }
    } catch (error) {
      console.error(error);
      setShowError("We couldn’t save the date. Please try again.");
      showSnackbar("Unable to save date.", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageShell maxWidth="md">

      <SurfaceCard sx={{ p: { xs: 2.5, md: 4 } }}>
        <form onSubmit={submit}>
          <Stack spacing={2.4}>
            <ToggleButtonGroup
              exclusive
              value={type}
              onChange={(_, nextValue) => nextValue && setType(nextValue)}
              fullWidth
              color="primary"
            >
              <ToggleButton value="single">One-Time Event</ToggleButton>
              <ToggleButton value="recurring">Recurring Event</ToggleButton>
            </ToggleButtonGroup>

            <TextField
              label="Event title"
              placeholder="e.g. Final Semester Exam"
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
                setShowError("");
              }}
              fullWidth
            />

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label="Event date"
                type="date"
                value={date}
                onChange={(e) => {
                  setDate(e.target.value);
                  setShowError("");
                }}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Time"
                type="time"
                value={time}
                onChange={(e) => {
                  setTime(e.target.value);
                  setShowError("");
                }}
                fullWidth
                InputLabelProps={{ shrink: true }}
                helperText="Optional"
              />
            </Stack>

            <TextField
              select
              label="Category"
              value={category}
              onChange={(e) => {
                setCategory(e.target.value);
                setShowError("");
              }}
            >
              {["Medical", "Exam", "Legal", "Personal", "Other"].map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="Notes"
              placeholder="Context, location, or any details you want to keep attached."
              value={notes}
              onChange={(e) => {
                setNotes(e.target.value);
                setShowError("");
              }}
              multiline
              minRows={4}
            />

            {type === "recurring" && (
              <Alert icon={<AutoAwesomeRounded />} severity="info">
                Recurring events are treated as yearly celebrations and appear in the
                celebrations view.
              </Alert>
            )}

            {showError && <Alert severity="error">{showError}</Alert>}

            <Stack direction={{ xs: "column-reverse", sm: "row" }} spacing={1.2} justifyContent="flex-end">
              <Button
                type="button"
                variant="text"
                color="inherit"
                onClick={() => (window.history.length > 1 ? navigate(-1) : navigate("/"))}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                startIcon={saving ? <CircularProgress size={18} sx={{ color: "white" }} /> : <SaveRounded />}
                disabled={!title.trim() || !date || saving}
              >
                {saving ? "Saving..." : "Save Date"}
              </Button>
            </Stack>
          </Stack>
        </form>
      </SurfaceCard>
    </PageShell>
  );
};
